import { useState, useEffect, useCallback } from 'react';

// Simple hook to fetch user profile from local Laravel backend and expose loading/error state
export default function useUserProfile() {
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetchProfile = useCallback(async (signal) => {
    setLoading(true);
    setError(null);

    // Only attempt to fetch a real profile if an access token is present.
    const token = localStorage.getItem('access_token');
    if (!token) {
      // Not authenticated — do not perform demo/mock fetch. Keep profile null.
      setProfileData(null);
      setLoading(false);
      return;
    }

    try {
      // Real API call can be used here. For local development the previous implementation
      // mocked a demo user unconditionally which caused unintended auto-auth. We avoid
      // auto-mocking unless an explicit DEMO flag is set.
      // const res = await fetch('http://127.0.0.1:8000/api/v1/user/profile', { signal, credentials: 'include' });
      // const json = await res.json();

      await new Promise(resolve => setTimeout(resolve, 200));
      // If you want to enable demo mocking for development, set VITE_DEMO_MODE=true and
      // return a fake user here. By default we do NOT mock and rely on a real token.
      const demoMode = import.meta.env.VITE_DEMO_MODE === 'true';
      if (demoMode) {
        const json = {
          id: 1,
          name: 'Demo User',
          email: 'demo@example.com',
        };
        console.log('[DEMO MODE] Mocked user profile response:', json);
        setProfileData(json);
      } else {
        // No demo mode and token exists — we should ideally fetch the profile from the server.
        // For now, set profileData to null and leave loading false. Calling code can use refetch.
        setProfileData(null);
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        console.debug('Profile fetch cancelled');
        return;
      }
      console.error('Profile fetch failed:', {
        name: err.name,
        message: err.message,
        endpoint: 'API: /users/me',
        credentialsMode: 'include',
      });
      setError(err);
      setProfileData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    fetchProfile(ac.signal);
    return () => ac.abort();
  }, [fetchProfile]);

  return { profileData, loading, error, refetch: fetchProfile };
}